import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Customers = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [customers, setCustomers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    customerid: null,
    companyid: companyId || null,
    customercode: '',
    customername: '',
    customertype: 'Credit',
    address: '',
    countryid: '',
    stateid: '',
    cityid: '',
    phone: '',
    whatsappno: '',
    email: '',
    taxnumber: '',
    creditlimit: '',
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchCountries();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, customers]);

  // Load states when country changes
  useEffect(() => {
    if (formData.countryid) {
      fetchStates(formData.countryid);
    } else {
      setStates([]);
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryid]);

  // Load cities when state changes
  useEffect(() => {
    if (formData.stateid) {
      fetchCities(formData.stateid);
    } else {
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.stateid]);

  const fetchCountries = async () => {
    try {
      const response = await apiClient.get('/Countries.php', {
        params: { active: 1 }
      });
      
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await apiClient.get('/States.php', {
        params: { countryid: countryId, active: 1 }
      });
      
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      const response = await apiClient.get('/Cities.php', {
        params: { stateid: stateId, active: 1 }
      });
      
      if (response.data.success) {
        setCities(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/Customers.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        setCustomers(response.data.data || []);
      } else {
        alert('Failed to fetch customers: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching customers. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = customers;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(customer => customer.customertype === filterType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(customer =>
        customer.customername.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.customercode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.whatsappno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCustomers(filtered);
  };

  const handleCountryChange = (countryId) => {
    setFormData({
      ...formData,
      countryid: countryId,
      stateid: '',
      cityid: ''
    });
  };

  const handleStateChange = (stateId) => {
    setFormData({
      ...formData,
      stateid: stateId,
      cityid: ''
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customername.trim()) {
      newErrors.customername = 'Customer name is required';
    }
    
    if (!formData.customertype) {
      newErrors.customertype = 'Customer type is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.phone && formData.phone.length < 7) {
      newErrors.phone = 'Phone number must be at least 7 digits';
    }

    if (formData.whatsappno && formData.whatsappno.length < 7) {
      newErrors.whatsappno = 'WhatsApp number must be at least 7 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Customers.php', {
        ...formData,
        companyid: companyId,
        isactive: formData.isactive ? 1 : 0,
        countryid: formData.countryid || null,
        stateid: formData.stateid || null,
        cityid: formData.cityid || null,
        creditlimit: formData.creditlimit || null
      });
      
      const data = response.data;
      
      if (data.success) {
        if (!editMode && data.data && data.data.customercode) {
          alert(`Customer created successfully!\nCustomer Code: ${data.data.customercode}`);
        } else {
          alert(editMode ? 'Customer updated successfully!' : 'Customer created successfully!');
        }
        setShowModal(false);
        resetForm();
        fetchCustomers();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving customer. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (customer) => {
    setFormData({
      customerid: customer.customerid,
      companyid: customer.companyid,
      customercode: customer.customercode,
      customername: customer.customername,
      customertype: customer.customertype,
      address: customer.address || '',
      countryid: customer.countryid || '',
      stateid: customer.stateid || '',
      cityid: customer.cityid || '',
      phone: customer.phone || '',
      whatsappno: customer.whatsappno || '',
      email: customer.email || '',
      taxnumber: customer.taxnumber || '',
      creditlimit: customer.creditlimit || '',
      isactive: customer.isactive === 1
    });
    
    // Load states and cities for existing customer
    if (customer.countryid) {
      await fetchStates(customer.countryid);
    }
    if (customer.stateid) {
      await fetchCities(customer.stateid);
    }
    
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/Customers.php', {
        data: { customerid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Customer deleted successfully!');
        fetchCustomers();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting customer. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      customerid: null,
      companyid: companyId || null,
      customercode: '',
      customername: '',
      customertype: 'Credit',
      address: '',
      countryid: '',
      stateid: '',
      cityid: '',
      phone: '',
      whatsappno: '',
      email: '',
      taxnumber: '',
      creditlimit: '',
      isactive: true
    });
    setStates([]);
    setCities([]);
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Customers Master</h2>
          <p className="subtitle">Manage customer and client information</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Customer
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name, code, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="CR">Credit</option>
            <option value="CS">Cash</option>
          </select>
        </div>

        <div className="toolbar-info">
          Total: {filteredCustomers.length} {filteredCustomers.length === 1 ? 'customer' : 'customers'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading customers...</div>
      ) : filteredCustomers.length === 0 ? (
        <div className="empty-state">
          <p>No customers found</p>
          {(searchTerm || filterType !== 'all') && (
            <button onClick={() => { setSearchTerm(''); setFilterType('all'); }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Customer Name</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.customerid}>
                  <td className="font-mono">{customer.customercode}</td>
                  <td className="font-semibold">{customer.customername}</td>
                  <td>
                    <span className={`type-badge ${customer.customertype.toLowerCase()}`}>
                      {customer.customertype}
                    </span>
                  </td>
                  <td className="contact-cell">
                    {customer.phone && (
                      <div className="contact-item">
                        <Phone size={14} />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.whatsappno && (
                      <div className="contact-item">
                        <MessageCircle size={14} />
                        <span>{customer.whatsappno}</span>
                      </div>
                    )}
                    {customer.email && (
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{customer.email}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontSize: '0.875rem' }}>
                      {customer.cityname && <div>{customer.cityname}</div>}
                      {customer.statename && <div style={{ color: '#666' }}>{customer.statename}</div>}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${customer.isactive ? 'active' : 'inactive'}`}>
                      {customer.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(customer)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(customer.customerid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Customer' : 'Add New Customer'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>Basic Information</h4>
                {editMode && formData.customercode && (
                  <div className="form-group">
                    <label>Customer Code (Auto-generated)</label>
                    <input
                      type="text"
                      value={formData.customercode}
                      disabled
                      style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customername">
                      Customer Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="customername"
                      value={formData.customername}
                      onChange={(e) => setFormData({ ...formData, customername: e.target.value })}
                      maxLength="200"
                      placeholder="e.g., ABC Trading Company"
                      className={errors.customername ? 'error' : ''}
                    />
                    {errors.customername && (
                      <span className="error-message">{errors.customername}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="customertype">
                      Customer Type <span className="required">*</span>
                    </label>
                    <select
                      id="customertype"
                      value={formData.customertype}
                      onChange={(e) => setFormData({ ...formData, customertype: e.target.value })}
                      className={errors.customertype ? 'error' : ''}
                    >
                      <option value="CR">Credit</option>
                      <option value="CS">Cash</option>
                    </select>
                    {errors.customertype && (
                      <span className="error-message">{errors.customertype}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Contact Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">
                      <Phone size={16} style={{ display: 'inline', marginRight: '5px' }} />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      maxLength="50"
                      placeholder="+966-50-1234567"
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && (
                      <span className="error-message">{errors.phone}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="whatsappno">
                      <MessageCircle size={16} style={{ display: 'inline', marginRight: '5px' }} />
                      WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      id="whatsappno"
                      value={formData.whatsappno}
                      onChange={(e) => setFormData({ ...formData, whatsappno: e.target.value })}
                      maxLength="50"
                      placeholder="+966-50-1234567"
                      className={errors.whatsappno ? 'error' : ''}
                    />
                    {errors.whatsappno && (
                      <span className="error-message">{errors.whatsappno}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <Mail size={16} style={{ display: 'inline', marginRight: '5px' }} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    maxLength="100"
                    placeholder="contact@example.com"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h4>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  Address Information
                </h4>
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="2"
                    maxLength="500"
                    placeholder="Full address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="countryid">Country</label>
                    <select
                      id="countryid"
                      value={formData.countryid}
                      onChange={(e) => handleCountryChange(e.target.value)}
                    >
                      <option value="">-- Select Country --</option>
                      {countries.map((country) => (
                        <option key={country.countryid} value={country.countryid}>
                          {country.countryname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="stateid">State</label>
                    <select
                      id="stateid"
                      value={formData.stateid}
                      onChange={(e) => handleStateChange(e.target.value)}
                      disabled={!formData.countryid}
                    >
                      <option value="">-- Select State --</option>
                      {states.map((state) => (
                        <option key={state.stateid} value={state.stateid}>
                          {state.statename}
                        </option>
                      ))}
                    </select>
                    {!formData.countryid && (
                      <small style={{ color: '#999', marginTop: '4px', display: 'block' }}>
                        Select country first
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="cityid">City</label>
                    <select
                      id="cityid"
                      value={formData.cityid}
                      onChange={(e) => setFormData({ ...formData, cityid: e.target.value })}
                      disabled={!formData.stateid}
                    >
                      <option value="">-- Select City --</option>
                      {cities.map((city) => (
                        <option key={city.cityid} value={city.cityid}>
                          {city.cityname}
                        </option>
                      ))}
                    </select>
                    {!formData.stateid && (
                      <small style={{ color: '#999', marginTop: '4px', display: 'block' }}>
                        Select state first
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Business Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="taxnumber">Tax Number / VAT Number</label>
                    <input
                      type="text"
                      id="taxnumber"
                      value={formData.taxnumber}
                      onChange={(e) => setFormData({ ...formData, taxnumber: e.target.value })}
                      maxLength="50"
                      placeholder="e.g., 300123456700003"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="creditlimit">Credit Limit</label>
                    <input
                      type="number"
                      id="creditlimit"
                      value={formData.creditlimit}
                      onChange={(e) => setFormData({ ...formData, creditlimit: e.target.value })}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;